<template>
  <div
    ref="preview"
    class="macro-preview absolute w-64 rounded-md bg-white dark:bg-slate-800 shadow-lg right-0 overflow-y-auto p-4 text-left rtl:text-right z-50 mr-[12%]"
    :class="{ 'top-auto': shouldPositionAbove }"
    :style="previewStyle"
  >
    <h6 class="text-sm text-slate-800 dark:text-slate-100 mb-4">
      {{ macro.name }}
    </h6>
    <div
      v-for="(action, i) in resolvedMacro"
      :key="i"
      class="relative pl-4 macro-block"
    >
      <div
        v-if="i !== macro.actions.length - 1"
        class="top-[0.390625rem] absolute -bottom-1 left-0 w-px bg-slate-75 dark:bg-slate-600"
      />
      <div
        class="absolute -left-[0.21875rem] top-[0.2734375rem] w-2 h-2 rounded-full bg-white dark:bg-slate-200 border-2 border-solid border-slate-100 dark:border-slate-600"
      />
      <p class="text-xs text-slate-500 dark:text-slate-400 mb-1">
        {{ action.actionName }}
      </p>
      <p class="text-slate-800 dark:text-slate-100">{{ action.actionValue }}</p>
    </div>
  </div>
</template>

<script>
import {
  resolveActionName,
  resolveTeamIds,
  resolveLabels,
  resolveAgents,
} from 'dashboard/routes/dashboard/settings/macros/macroHelper';
import { mapGetters } from 'vuex';

export default {
  props: {
    macro: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      shouldPositionAbove: false,
      maxHeightStyle: '22.5rem',
    };
  },
  mounted() {
    this.$nextTick(() => {
      this.checkPosition();
    });
  },
  computed: {
    resolvedMacro() {
      return this.macro.actions.map(action => {
        return {
          actionName: resolveActionName(action.action_name),
          actionValue: this.getActionValue(
            action.action_name,
            action.action_params
          ),
        };
      });
    },
    previewStyle() {
      const baseStyle = {
        maxHeight: this.maxHeightStyle,
      };
      
      if (this.shouldPositionAbove) {
        // Position above with proper bottom spacing
        baseStyle.bottom = 'calc(100% + 1.25rem)'; // 1.25rem = 20px spacing from parent bottom
        baseStyle.top = 'auto';
      } else {
        // Position below with proper top spacing
        baseStyle.top = 'calc(100% + 0.5rem)'; // 0.5rem = 8px spacing from parent top
        baseStyle.bottom = 'auto';
      }
      
      return baseStyle;
    },
    ...mapGetters({
      labels: 'labels/getLabels',
      teams: 'teams/getTeams',
      agents: 'agents/getAgents',
      emailTemplates: 'emailTemplates/getEmailTemplates',
    }),
  },
  methods: {
    checkPosition() {
      if (!this.$refs.preview) return;
      
      // Get the macro item position to calculate available space
      const macroItem = this.$refs.preview.closest('.macro');
      if (!macroItem) return;
      
      const itemRect = macroItem.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - itemRect.bottom;
      const spaceAbove = itemRect.top;
      
      // Minimum space needed below to show preview (preview height + spacing)
      const minSpaceNeeded = 380; // 360px max height + 20px spacing
      const bottomSpacing = 20; // Space from bottom when positioned above
      const topSpacing = 20; // Space from top when positioned below
      
      // Calculate max height based on available space
      const minHeight = 150; // Minimum height in pixels
      const maxHeight = 360; // Maximum height (22.5rem = 360px)
      
      let availableSpace;
      let shouldPositionAbove = false;
      
      // Check if there's enough space below
      if (spaceBelow < minSpaceNeeded) {
        // Not enough space below, check if we can position above
        if (spaceAbove > minSpaceNeeded) {
          // Position above - use space above minus spacing
          shouldPositionAbove = true;
          availableSpace = spaceAbove - bottomSpacing - topSpacing;
        } else {
          // Not enough space either way, use whatever space is available below
          shouldPositionAbove = false;
          availableSpace = spaceBelow - topSpacing;
        }
      } else {
        // Enough space below, position below
        shouldPositionAbove = false;
        availableSpace = spaceBelow - topSpacing;
      }
      
      // Set max height within bounds
      const calculatedHeight = Math.max(minHeight, Math.min(maxHeight, availableSpace));
      this.maxHeightStyle = `${calculatedHeight}px`;
      this.shouldPositionAbove = shouldPositionAbove;
    },
    resolveEmailTemplate(params) {
      if (!params || !params[0]) return '';
      const template = this.emailTemplates.find(t => t.id === params[0]);
      return template ? template.name : `Template #${params[0]}`;
    },
    kivoNotificationPreview(params) {
      const config = Array.isArray(params) ? params[0] : params;
      if (!config) return '';
      const url = config.url || '';
      const text = config.text || '';
      if (!url && !text) return '';
      return text ? `${url} — ${text}` : url;
    },
    getActionValue(key, params) {
      const actionsMap = {
        assign_team: resolveTeamIds(this.teams, params),
        add_label: resolveLabels(this.labels, params),
        remove_label: resolveLabels(this.labels, params),
        assign_agent: resolveAgents(this.agents, params),
        mute_conversation: null,
        snooze_conversation: null,
        resolve_conversation: null,
        remove_assigned_team: null,
        send_webhook_event: params[0],
        send_kivo_notification: this.kivoNotificationPreview(params),
        send_message: params[0],
        send_email_transcript: params[0],
        add_private_note: params[0],
        email_template: this.resolveEmailTemplate(params),
      };
      return actionsMap[key] || '';
    },
  },
};
</script>

<style lang="scss" scoped>
.macro-preview {
  .macro-block {
    &:not(:last-child) {
      @apply pb-2;
    }
  }
}
</style>
